import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, XCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';

const CheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const setup = searchParams.get('setup');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('No session ID found in URL');
      return;
    }

    const completeTrialSignup = async () => {
      try {
        console.log('[CheckoutSuccess] Processing session:', sessionId);
        
        // Call the complete-trial-signup function
        const { data, error } = await supabase.functions.invoke('complete-trial-signup', {
          body: { sessionId }
        });

        if (error) {
          console.error('[CheckoutSuccess] Error:', error);
          setStatus('error');
          setMessage(error.message || 'Failed to complete trial signup');
          return;
        }

        if (!data.success) {
          console.error('[CheckoutSuccess] Function returned error:', data);
          setStatus('error');
          setMessage(data.error || 'Failed to complete trial signup');
          return;
        }

        console.log('[CheckoutSuccess] Trial signup completed successfully:', data);
        setStatus('success');
        setMessage('Your trial has been activated successfully!');
        setDetails(data);

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          const dashboardUrl = setup === 'new' ? '/dashboard?setup=new' : '/dashboard';
          navigate(dashboardUrl);
        }, 3000);

      } catch (error) {
        console.error('[CheckoutSuccess] Unexpected error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
      }
    };

    completeTrialSignup();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    setStatus('loading');
    setMessage('');
    setDetails(null);
    
    // Re-run the completion logic
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      window.location.reload();
    }
  };

  const handleGoToDashboard = () => {
    const setup = searchParams.get('setup');
    const dashboardUrl = setup === 'new' ? '/dashboard?setup=new' : '/dashboard';
    navigate(dashboardUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === 'loading' && (
                <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              )}
              {status === 'error' && (
                <XCircle className="h-16 w-16 text-red-500" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold">
              {status === 'loading' && 'Processing Your Trial...'}
              {status === 'success' && 'Welcome to Hivetivity!'}
              {status === 'error' && 'Something Went Wrong'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {status === 'loading' && (
              <div className="text-center">
                <p className="text-gray-600">
                  We're setting up your trial account. This should only take a moment...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-4">
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {message}
                  </AlertDescription>
                </Alert>
                
                {details && (
                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    <p><strong>Status:</strong> {details.status}</p>
                    {details.trialEndsAt && (
                      <p><strong>Trial ends:</strong> {new Date(details.trialEndsAt).toLocaleDateString()}</p>
                    )}
                  </div>
                )}
                
                <p className="text-center text-gray-600">
                  You'll be redirected to your dashboard in a few seconds...
                </p>
                
                <div className="flex justify-center">
                  <Button onClick={handleGoToDashboard}>
                    Go to Dashboard Now
                  </Button>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {message}
                  </AlertDescription>
                </Alert>
                
                <p className="text-center text-gray-600">
                  Don't worry - your payment was processed successfully. We just need to activate your trial.
                </p>
                
                <div className="flex gap-2 justify-center">
                  <Button onClick={handleRetry} variant="outline">
                    Try Again
                  </Button>
                  <Button onClick={handleGoToDashboard}>
                    Go to Dashboard
                  </Button>
                </div>
                
                <p className="text-xs text-center text-gray-500">
                  If this problem persists, please contact support with session ID: {searchParams.get('session_id')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default CheckoutSuccess;